export interface FAQ {
  id: string;
  category: string;
  question: string;
  answer: string;
}

export const DEALER_FAQS: FAQ[] = [
  // Data & Reporting Questions
  {
    id: 'data-01',
    category: 'Data & Reporting',
    question: 'What do the impression numbers mean for my dealership?',
    answer: 'Impressions represent how many times your ads were displayed to potential customers. For example, if your Display advertising shows 5,000 impressions, it means your dealership ads were seen 5,000 times across websites, apps, and other digital platforms within your selected radius and time period.'
  },
  {
    id: 'data-02',
    category: 'Data & Reporting',
    question: 'Why do I see different numbers for different advertising types?',
    answer: 'Each advertising type reaches customers in different ways. Display ads appear on websites, DOOH shows on digital billboards, OTT/CTV plays on streaming TV, YouTube appears on video content, and Radio runs on audio platforms. The numbers vary because each channel has different reach and frequency in your market area.'
  },
  {
    id: 'data-03',
    category: 'Data & Reporting',
    question: 'How often is my data updated?',
    answer: 'Your reporting data is updated daily with the latest information from all advertising campaigns. This ensures you always have current insights into how your marketing efforts are performing in your local market.'
  },
  {
    id: 'data-04',
    category: 'Data & Reporting',
    question: 'What does the map show me?',
    answer: 'The map displays where your advertising is reaching potential customers geographically. Different colored pins represent different types of advertising, and the concentration shows you which areas around your dealership are seeing the most ad exposure. This helps you understand your marketing coverage area.'
  },
  {
    id: 'data-05',
    category: 'Data & Reporting',
    question: 'Can I change the radius to see a different area?',
    answer: 'Yes! Use the radius selector in the top right to adjust your viewing area from 10 to 100 miles around your dealership. This helps you see how far your advertising reaches and can inform decisions about expanding your marketing area or focusing on closer neighborhoods.'
  },

  // Campaign Performance Questions
  {
    id: 'performance-01',
    category: 'Campaign Performance',
    question: 'What\'s a good number of impressions for my dealership?',
    answer: 'Impression goals vary by market size, season, and business objectives. Generally, consistent daily impressions across multiple channels (Display, DOOH, OTT/CTV, YouTube) indicate healthy campaign performance. Focus on trends over time and seasonal patterns that align with peak lawn care and snow removal seasons.'
  },
  {
    id: 'performance-02',
    category: 'Campaign Performance',
    question: 'Why do my numbers go up and down throughout the week?',
    answer: 'Advertising performance naturally fluctuates based on when people are most active online and consuming media. Weekends might show different patterns than weekdays, and seasonal factors affect when people are thinking about lawn care or snow removal equipment.'
  },
  {
    id: 'performance-03',
    category: 'Campaign Performance',
    question: 'How do I know if my advertising is working?',
    answer: 'Look for consistent impression delivery, geographic coverage that matches your service area, and seasonal alignment with your business needs. Rising trends during peak seasons (spring for lawn equipment, fall/winter for snow equipment) are positive indicators. Also monitor foot traffic and sales inquiries for correlation with advertising peaks.'
  },
  {
    id: 'performance-04',
    category: 'Campaign Performance',
    question: 'What should I do if my numbers seem low?',
    answer: 'Low impression numbers could indicate several things: seasonal timing, market competition, or campaign settings. Contact your marketing coordinator to review your campaigns, discuss expanding your radius, increasing budget during peak seasons, or adjusting targeting to reach more potential customers in your area.'
  },

  // Technical Questions
  {
    id: 'technical-01',
    category: 'Technical',
    question: 'Why don\'t I see any radio data sometimes?',
    answer: 'Radio advertising may not run continuously - it\'s often scheduled for specific time periods, peak seasons, or promotional campaigns. If you don\'t see radio data, it may mean no radio spots were scheduled during your selected date range, or radio isn\'t part of your current marketing mix.'
  },
  {
    id: 'technical-02',
    category: 'Technical',
    question: 'What browsers work best with this dashboard?',
    answer: 'This dashboard works best with modern browsers like Chrome, Firefox, Safari, or Edge. Make sure your browser is updated to the latest version for optimal performance. If you experience issues, try refreshing the page or clearing your browser cache.'
  },
  {
    id: 'technical-03',
    category: 'Technical',
    question: 'Can I export or share this data?',
    answer: 'Yes! Look for export buttons throughout the dashboard to download data in PDF or Excel formats. This makes it easy to share performance information with your team, include in business reports, or keep for your own records.'
  },

  // Business Questions  
  {
    id: 'business-01',
    category: 'Business Impact',
    question: 'How does this advertising help my dealership?',
    answer: 'This coordinated advertising approach increases your dealership\'s visibility across multiple touchpoints where customers spend time. When someone needs a lawn mower or snow blower, they\'re more likely to think of your dealership because they\'ve seen your brand on websites, streaming TV, digital billboards, and heard you on radio.'
  },
  {
    id: 'business-02',
    category: 'Business Impact',
    question: 'When should I expect to see the biggest impact?',
    answer: 'Advertising impact typically aligns with seasonal demand. Expect higher activity in spring (March-May) for lawn care equipment and fall/winter (October-February) for snow removal equipment. Plan inventory and staffing around these seasonal advertising pushes.'
  },
  {
    id: 'business-03',
    category: 'Business Impact',
    question: 'How do I use this information to help my business?',
    answer: 'Use this data to understand your marketing reach, plan inventory based on seasonal trends, and identify opportunities. If you see strong advertising performance but low foot traffic, consider promotional offers. If certain areas show high impression counts, consider service expansion or targeted local events.'
  },
  {
    id: 'business-04',
    category: 'Business Impact',
    question: 'Who do I contact if I have questions about my campaigns?',
    answer: 'Contact your dedicated marketing coordinator or account manager for campaign questions, strategy discussions, or to request changes. They can help optimize your advertising mix, adjust targeting, or provide additional insights based on your specific market and business goals.'
  }
];

export const getFaqsByCategory = (): Record<string, FAQ[]> => {
  return DEALER_FAQS.reduce((acc, faq) => {
    if (!acc[faq.category]) {
      acc[faq.category] = [];
    }
    acc[faq.category].push(faq);
    return acc;
  }, {} as Record<string, FAQ[]>);
};